import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";
import { visibilityFor } from "@wato/business-types";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Button, Icon, IconButton, Row } from "@wato/ui";
import { useApi } from "../api/context";
import type { UiLayerKey } from "../api/onboarding-types";
import { mayUse } from "../features/roles/role-label";
import { useOptionalWorkspace } from "../features/workspace/WorkspaceContext";

/**
 * `autoStart`: open on the first visit. The inbox tour does not: that page now explains itself
 * in place (collapsed intro + empty states), so a floating card would only cover the working
 * area. It stays available from the Help menu, where the user asked for it.
 */
export const TOURS = {
  home: { path: "", steps: 3, autoStart: false },
  inbox: { path: "conversations", steps: 3, autoStart: false },
  contacts: { path: "contacts", steps: 3, autoStart: false },
  flows: { path: "flows", steps: 3, autoStart: true },
  broadcasts: { path: "broadcasts", steps: 3, autoStart: true },
  sell: { path: "sell", steps: 3, autoStart: false },
} as const;
export type TourKey = keyof typeof TOURS;

const HEADER = "[data-page-header]";
const HELP_CHIP = "[data-feature-intro]";
/** Existing stable element each step points at (null = no highlight). A missing element is ignored. */
export const TOUR_ANCHORS: Record<TourKey, readonly (string | null)[]> = {
  home: ['[data-tour-target="home-next"]', '[data-tour-target="home-kpis"]', HELP_CHIP],
  inbox: [HEADER, null, HELP_CHIP],
  contacts: [HEADER, null, HELP_CHIP],
  flows: [HEADER, null, HELP_CHIP],
  broadcasts: [HEADER, null, HELP_CHIP],
  sell: ['[data-testid="sell-card"]', '[data-testid="sell-status"]', HELP_CHIP],
};

/** The permissions that open each tour's page (any one); without them the page shows «no access». */
const TOUR_PERMISSION: Record<TourKey, readonly string[]> = {
  home: [],
  flows: ["flows.view"],
  inbox: ["inbox.view"],
  contacts: ["contacts.view"],
  broadcasts: ["broadcasts.view"],
  sell: ["commerce.view", "channels.view", "integrations.view"],
};

/** The @wato/business-types feature key of each tour's page (the nav uses the same keys). */
const TOUR_FEATURE: Record<TourKey, string> = {
  home: "home",
  inbox: "conversations",
  contacts: "task:reply",
  flows: "flows",
  broadcasts: "task:offer",
  sell: "task:sell",
};

/** A tour for a page the member cannot open would float over the blocked state. */
export function mayTakeTour(
  workspace: Parameters<typeof mayUse>[0] | null,
  tour: TourKey,
): boolean {
  if (workspace === null) return true;
  if (visibilityFor(workspace.businessType ?? null, TOUR_FEATURE[tour]) === "hide") return false;
  const needed = TOUR_PERMISSION[tour];
  return needed.length === 0 || needed.some((p) => mayUse(workspace, p));
}

/** Presentational tour card: step copy from `setup.tour.<tour>.<n>.*`, skippable at any step. */
export function ProductTour({ tour, onClose }: { tour: TourKey; onClose: () => void }) {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const total = TOURS[tour].steps;
  const step = index + 1;
  const key = (part: string) => `setup.tour.${tour}.${step}.${part}` as MessageKey;
  const anchor = TOUR_ANCHORS[tour][index] ?? null;
  useEffect(() => {
    const el = anchor ? document.querySelector<HTMLElement>(anchor) : null;
    if (!el) return;
    const classes = ["outline", "outline-2", "outline-offset-2", "outline-focus"];
    const added = classes.filter((c) => !el.classList.contains(c));
    el.classList.add(...added);
    el.scrollIntoView?.({ block: "nearest" });
    return () => {
      el.classList.remove(...added);
    };
  }, [anchor]);
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t(`setup.tour.${tour}.name` as MessageKey)}
      data-tour={tour}
      className="fixed bottom-4 end-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface p-4 shadow-lg"
    >
      <p className="text-xs text-fg-muted">{t("setup.tour.stepOf", { step, total })}</p>
      <h4 className="text-base font-semibold text-fg">{t(key("title"))}</h4>
      <p className="mb-3 text-sm text-fg-muted">{t(key("body"))}</p>
      <Row gap={2} justify="between">
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t("setup.tour.skip")}
        </Button>
        <Row gap={2}>
          {index > 0 ? (
            <IconButton
              label={t("setup.tour.back")}
              icon={<Icon name="chevron-back" />}
              variant="secondary"
              onClick={() => setIndex(index - 1)}
            />
          ) : null}
          {step < total ? (
            <Button size="sm" onClick={() => setIndex(index + 1)}>
              {t("setup.tour.next")}
            </Button>
          ) : (
            <Button size="sm" onClick={onClose}>
              {t("setup.tour.finish")}
            </Button>
          )}
        </Row>
      </Row>
    </div>
  );
}

/**
 * Shell-level host: starts a section's tour on the first visit (per workspace + browser), and
 * renders the help menu that re-launches any tour (navigating to its section first).
 */
export function ProductTourHost({
  workspaceId,
  embedded = false,
  onPick,
}: {
  workspaceId: string;
  /** FB3-20: render only the tour list (no own button) so the header help menu can hold it. */
  embedded?: boolean;
  onPick?: () => void;
}) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const workspace = useOptionalWorkspace()?.workspace ?? null;
  const [active, setActive] = useState<TourKey | null>(null);
  const [seen, setSeen] = useState<ReadonlySet<string> | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let live = true;
    api.getUiPrefs(workspaceId).then(
      (prefs) => live && setSeen(new Set(Object.keys(prefs.dismissed))),
      () => live && setSeen(null), // unknown: never auto-start (no clutter on a failed read)
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId]);

  useEffect(() => {
    if (!seen) return;
    const segment = pathname.split("/")[3] ?? "";
    const match = (Object.keys(TOURS) as TourKey[]).find(
      (k) => TOURS[k].path === segment && pathname.split("/").length === (segment === "" ? 3 : 4),
    );
    if (
      match &&
      TOURS[match].autoStart &&
      !seen.has(`tour.${match}`) &&
      mayTakeTour(workspace, match)
    )
      setActive(match);
  }, [pathname, seen, workspace]);

  const start = (tour: TourKey) => {
    navigate(`/w/${workspaceId}/${TOURS[tour].path}`);
    if (mayTakeTour(workspace, tour)) setActive(tour);
  };

  const close = () => {
    if (active) {
      const layer = `tour.${active}` as UiLayerKey;
      setSeen((prev) => new Set(prev).add(layer));
      void api.dismissUiLayer(workspaceId, layer).catch(() => undefined);
    }
    setActive(null);
  };

  return (
    <>
      {embedded ? (
        <ul aria-label={t("setup.tour.menu")} className="p-1">
          {(Object.keys(TOURS) as TourKey[]).map((tour) => (
            <li key={tour}>
              <button
                type="button"
                className="w-full rounded-md px-3 py-2 text-start text-sm text-fg hover:bg-surface-sunken"
                onClick={() => {
                  onPick?.();
                  start(tour);
                }}
              >
                {t(`setup.tour.${tour}.name` as MessageKey)}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="relative">
          <button
            type="button"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-md px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-sunken"
          >
            {t("setup.tour.help")}
          </button>
          {menuOpen ? (
            <ul
              aria-label={t("setup.tour.menu")}
              className="absolute end-0 z-40 mt-1 w-48 rounded-md border border-line bg-surface p-1 shadow-lg"
            >
              {(Object.keys(TOURS) as TourKey[]).map((tour) => (
                <li key={tour}>
                  <button
                    type="button"
                    className="w-full rounded-md px-3 py-2 text-start text-sm text-fg hover:bg-surface-sunken"
                    onClick={() => {
                      setMenuOpen(false);
                      start(tour);
                    }}
                  >
                    {t(`setup.tour.${tour}.name` as MessageKey)}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      {/* Portaled: the embedded host sits in the help menu's hidden panel, which would hide the card. */}
      {active
        ? createPortal(<ProductTour key={active} tour={active} onClose={close} />, document.body)
        : null}
    </>
  );
}
